import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouteMatch } from 'react-router-dom';
import type { SectionLink, SectionLinksObject } from 'src/models/section-links';
import { useUser } from 'src/hooks/use-user';
import { useStores } from 'src/hooks/use-stores';
import { useGetEnterpriseQuery } from 'types/graphql';

const useEnterpriseAdminLinks = (externalAuthorizationEnabled?: boolean): SectionLinksObject => {
  const User = useUser();
  const { apolloClient } = useStores();
  const flags = useFlags();
  const userHasEnterpriseSpecialsAccess = User.profile.permissions?.enterpriseSpecials ?? false;

  const showAdsSettings = User.hasAdsPermissions;
  const showPersonalizationSettings = User.hasPersonalizationPermissions;
  const showEnterpriseAssets = flags['growth.ecomm.enterprise-assets-view.rollout'] ?? false;
  const showAssetLibrary = flags['growth.ecomm.asset-library.rollout'] ?? false;
  const showEnterpriseMarketing = flags['growth.ecomm.enterprise-marketing-view.rollout'] ?? false;
  const showEnterpriseMenu = flags['ecomm.admin.sort-copy-to-locations.rollout'] ?? false;
  const showLocationGroups = flags['voice.enterprise-admin.location-groups.rollout'] ?? false;
  const showVoiceAgents = flags['voice.enterprise-admin.voice-agents.rollout'] ?? false;
  const showEnterpriseLegalDisclaimer = flags['ecomm.admin.enterprise-legal-disclaimer.rollout'] ?? false;
  const showReviewsModeration = flags['growth.ecomm.admin-product-reviews-moderation.rollout'] ?? false;
  const showDigitalDisplaysMedia = flags['growth.ecomm.digital-displays-admin-ui.rollout'] ?? false;
  const showWebStudio = flags['growth.ecomm.enterprise-web-studio-view.rollout'] ?? false;

  // Matchers
  const enterpriseRouteMatch = useRouteMatch<{ enterpriseId: string }>('/enterprise/:enterpriseId');
  const atAddSpecial = useRouteMatch<{ id: string }>('/enterprise/:enterpriseId/promote/specials/add');
  const atEditSpecial = useRouteMatch<{ id: string; specialId: string }>(
    '/enterprise/:enterpriseId/promote/specials/edit/:specialId'
  );
  const atOrderMatch = useRouteMatch<{ chainId: string; variant: string; orderId: string }>(
    '/chains/:chainId/orders/:variant/:orderId'
  );
  const enterpriseId = enterpriseRouteMatch?.params.enterpriseId;
  const { data: enterpriseData } = useGetEnterpriseQuery({
    client: apolloClient,
    skip: !enterpriseId,
    variables: { enterpriseId: enterpriseId ?? '' },
  });

  const enterprise = enterpriseData?.getEnterprise;
  const showEnrollmentSettings = enterprise?.hasDposLoyalty ?? false;

  if (!enterpriseRouteMatch) {
    console.error('enterpriseRouteMatch not found');
    return {
      links: [],
      redirectPath: '/redirect',
    };
  }

  const basePath = enterpriseRouteMatch.path;
  const baseTo = enterpriseRouteMatch.url;

  const links: SectionLink[] = [
    {
      backlink: atOrderMatch?.isExact ? `${baseTo}/orders/${atOrderMatch.params.variant}` : undefined,
      filePath: './orders',
      icon: 'orders',
      key: 'orders',
      label: 'Orders',
      routePath: `${basePath}/orders`,
      subHeaders: [
        { to: `${baseTo}/orders/main`, label: 'Online', visible: true },
        { to: `${baseTo}/orders/kiosk`, label: 'Kiosk', visible: true },
      ],
      to: `${baseTo}/orders`,
      visible: true,
    },
    {
      filePath: './dispensaries',
      icon: 'dispensaries',
      key: 'dispensaries',
      label: 'Locations',
      routePath: `${basePath}/dispensaries`,
      to: `${baseTo}/dispensaries`,
      visible: true,
      subHeaders: [
        { to: `${baseTo}/dispensaries`, label: 'All Locations', visible: true, exact: true },
        {
          to: `${baseTo}/dispensaries/groups`,
          label: 'Location Groups',
          visible: showLocationGroups && User.isSuperAdmin,
        },
      ],
    },
    {
      filePath: './assets',
      icon: 'assets',
      key: 'assets',
      label: 'Assets',
      routePath: `${basePath}/assets`,
      to: `${baseTo}/assets`,
      visible: showEnterpriseAssets,
      subHeaders: [
        { to: `${baseTo}/assets`, label: 'Overview', visible: true, exact: true },
        { to: `${baseTo}/assets/library`, label: 'Library', visible: showAssetLibrary },
        { to: `${baseTo}/assets/image-banners`, label: 'Image Banners', visible: true },
        { to: `${baseTo}/assets/text-banners`, label: 'Text Banners', visible: true },
        { to: `${baseTo}/assets/category-images`, label: 'Category Images', visible: true },
        { to: `${baseTo}/assets/logos`, label: 'Logos', visible: true },
        { to: `${baseTo}/assets/legal-disclaimer`, label: 'Legal Disclaimer', visible: showEnterpriseLegalDisclaimer },
      ],
    },
    {
      filePath: './marketing',
      icon: 'promote',
      key: 'marketing',
      label: 'Marketing',
      routePath: `${basePath}/marketing`,
      to: `${baseTo}/marketing`,
      visible: showEnterpriseMarketing,
      subHeaders: [
        { to: `${baseTo}/marketing`, label: 'Overview', visible: true, exact: true },
        { to: `${baseTo}/marketing/collections`, label: 'Collections', visible: true },
        { to: `${baseTo}/marketing/custom-categories`, label: 'Custom Categories', visible: true },
        { to: `${baseTo}/marketing/homepage-sections`, label: 'Homepage Sections', visible: true },
      ],
    },
    {
      filePath: './web-studio',
      icon: 'content',
      key: 'web-studio',
      label: 'Web Studio',
      routePath: `${basePath}/web-studio`,
      to: `${baseTo}/web-studio`,
      visible: showWebStudio,
    },
    {
      filePath: './media',
      icon: 'menu',
      key: 'media',
      label: 'Media',
      routePath: `${basePath}/media`,
      to: `${baseTo}/media`,
      visible: showDigitalDisplaysMedia,
      subHeaders: [
        { to: `${baseTo}/media`, label: 'Overview', visible: true, exact: true },
        { to: `${baseTo}/media/boards`, label: 'Boards', visible: true },
        { to: `${baseTo}/media/playlists`, label: 'Playlists', visible: true },
        { to: `${baseTo}/media/displays`, label: 'Displays', visible: true },
      ],
    },
    {
      filePath: './menu',
      icon: 'customize',
      key: 'menu',
      label: 'Menu',
      routePath: `${basePath}/menu`,
      to: `${baseTo}/menu`,
      visible: showEnterpriseMenu,
      subHeaders: [
        { to: `${baseTo}/menu/sort`, label: 'Default Sort', visible: true },
        { to: `${baseTo}/menu/brand-order`, label: 'Brand Order', visible: true },
      ],
    },
    {
      filePath: './voice-agents',
      icon: 'phone',
      key: 'voice-agents',
      label: 'Voice Agents',
      routePath: `${basePath}/voice-agents`,
      to: `${baseTo}/voice-agents`,
      visible: showVoiceAgents,
    },
    {
      filePath: './analytics',
      icon: 'analytics',
      key: 'analytics',
      label: 'Analytics',
      routePath: `${basePath}/analytics`,
      to: `${baseTo}/analytics`,
      subHeaders: [
        { to: `${baseTo}/analytics/sales`, label: 'Sales', visible: true },
        { to: `${baseTo}/analytics/products`, label: 'Products', visible: true },
        { to: `${baseTo}/analytics/brands`, label: 'Brands', visible: true },
        { to: `${baseTo}/analytics/ecommerce`, label: 'Ecommerce', visible: true },
        { to: `${baseTo}/analytics/marketing`, label: 'Marketing', visible: true },
      ],
      visible: User.isSuperAdmin || User.hasAnalyticsPermissions,
    },
    {
      backlink: atEditSpecial?.isExact || atAddSpecial?.isExact ? `${baseTo}/promote/specials` : undefined,
      filePath: './promote',
      icon: 'promote',
      key: 'promote',
      label: 'Promote',
      routePath: `${basePath}/promote`,
      subHeaders: [
        {
          label: 'Specials',
          to: `${baseTo}/promote/specials`,
          visible: !atAddSpecial?.isExact && !atEditSpecial?.isExact,
        },
      ],
      to: `${baseTo}/promote`,
      visible: User.isSuperAdmin || userHasEnterpriseSpecialsAccess,
    },
    {
      filePath: './reviews',
      icon: 'reviews',
      key: 'reviews',
      label: 'Reviews',
      routePath: `${basePath}/reviews`,
      to: `${baseTo}/reviews`,
      visible: showReviewsModeration,
    },
    {
      filePath: './settings',
      icon: 'settings',
      key: 'settings',
      label: 'Settings',
      routePath: `${basePath}/settings`,
      to: `${baseTo}/settings`,
      visible: true,
      subHeaders: [
        { to: `${baseTo}/settings/account`, label: 'My Account', visible: true },
        { to: `${baseTo}/settings/mobile-app`, label: 'Mobile App', visible: User.isSuperAdmin },
        {
          to: `${baseTo}/settings/authorization-provider`,
          label: 'Authorization Provider',
          visible: (User.isSuperAdmin || User.isEnterpriseAdmin) && !!externalAuthorizationEnabled,
        },
        {
          to: `${baseTo}/settings/ads`,
          label: 'Ads / Personalization',
          visible: showAdsSettings || showPersonalizationSettings,
        },
        {
          to: `${baseTo}/settings/enrollment`,
          label: 'Enrollment',
          visible: (User.isEnterpriseAdmin || User.isSuperAdmin) && showEnrollmentSettings,
        },
      ],
    },
  ];

  const visibleLinks = links.filter(({ visible }) => visible);

  return { links: visibleLinks, redirectPath: `${baseTo}/orders` };
};

export { useEnterpriseAdminLinks };
